import React, { useState, useEffect } from 'react';

const API = 'https://calendar.nareea.co.uk';

const ReleasePanel = () => {
  const [files, setFiles] = useState([]);
  const [releasing, setReleasing] = useState(false);
  const [releaseMsg, setReleaseMsg] = useState('');
  const [label, setLabel] = useState('');

  const loadFiles = () => {
    fetch(`${API}/api/release`)
      .then(r => r.json())
      .then(data => setFiles(data.files || []))
      .catch(console.error);
  };

  useEffect(() => { loadFiles(); }, []);

  const handleRelease = async () => {
    setReleasing(true);
    setReleaseMsg('');
    try {
      const res = await fetch(`${API}/api/release`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: label || new Date().toISOString().slice(0, 10) }),
      });
      const data = await res.json();
      if (data.success) {
        setReleaseMsg(`Released: ${data.filename} (${data.events_count} events)`);
        setLabel('');
        loadFiles();
      } else {
        setReleaseMsg(`Error: ${data.error}`);
      }
    } catch (err) {
      setReleaseMsg(`Error: ${err.message}`);
    } finally {
      setReleasing(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  const formatDate = (ts) => new Date(ts).toLocaleString('en', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
  });

  return (
    <div style={{ maxWidth: 700, margin: '20px auto', fontFamily: 'Google Sans, Arial, sans-serif', padding: '0 16px' }}>

      {/* Header */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 500, color: '#1a73e8' }}>
          Release for Luke
        </h3>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#5f6368' }}>
          Generate final ICS file from approved calendar. Luke imports it directly into Google Calendar.
        </p>

        {/* Release form */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            type="text"
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="Version label (e.g. week-may-25)"
            style={{ flex: 1, padding: '10px 14px', border: '1px solid #dadce0', borderRadius: 8, fontSize: 14, outline: 'none' }}
          />
          <button
            onClick={handleRelease}
            disabled={releasing}
            style={{ padding: '10px 24px', background: releasing ? '#dadce0' : '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: releasing ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
          >
            {releasing ? 'Generating...' : 'Release ICS'}
          </button>
        </div>

        {releaseMsg && (
          <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: releaseMsg.includes('Error') ? '#fce8e6' : '#e6f4ea', color: releaseMsg.includes('Error') ? '#c5221f' : '#137333', fontSize: 13 }}>
            {releaseMsg.includes('Error') ? releaseMsg : `✅ ${releaseMsg}`}
          </div>
        )}
      </div>

      {/* How it works */}
      <div style={{ background: '#e8f0fe', borderRadius: 12, padding: 16, marginBottom: 20, fontSize: 13, color: '#1a73e8' }}>
        <strong>How Luke uses this:</strong>
        <ol style={{ margin: '8px 0 0 16px', lineHeight: 1.8 }}>
          <li>EA reviews and approves calendar in EA Dashboard</li>
          <li>EA clicks "Release ICS" — generates final file</li>
          <li>Luke downloads ICS and imports into Google Calendar</li>
          <li>Or EA shares download link directly with Luke</li>
        </ol>
      </div>

      {/* Files list */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h4 style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>
            Released Versions ({files.length})
          </h4>
          <button onClick={loadFiles} style={{ background: 'none', border: '1px solid #dadce0', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer', color: '#5f6368' }}>
            Refresh
          </button>
        </div>

        {files.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: '#5f6368', fontSize: 13 }}>
            No releases yet — click "Release ICS" to generate first version
          </div>
        )}

        {files.map((file, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '14px 0', borderBottom: i < files.length - 1 ? '1px solid #f0f0f0' : 'none', gap: 12 }}>
            <div style={{ width: 40, height: 40, background: '#e8f0fe', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
              📅
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, fontSize: 14, color: '#3c4043', marginBottom: 3 }}>{file.name}</div>
              <div style={{ fontSize: 12, color: '#5f6368' }}>
                {formatSize(file.size)} · {formatDate(file.modified)}
              </div>
            </div>
            <a
              href={`${API}/api/release/${encodeURIComponent(file.name)}`}
              download={file.name}
              style={{ padding: '8px 16px', background: '#34a853', color: '#fff', borderRadius: 6, fontSize: 13, fontWeight: 500, textDecoration: 'none' }}
            >
              Download
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReleasePanel;

